import { taskGeneratorDeleteModal } from '@/components/tasks/TaskGeneratorDeleteModal.vue';
import { taskGeneratorPullChangesModal } from '@/components/tasks/TaskGeneratorPullChangesModal.vue';
import { taskGeneratorUpdateModal } from '@/components/tasks/TaskGeneratorUpdateModal.vue';
import { jsonStableStringify } from '../../compliance-shared/services/json-stable-stringify';
import {
  getGeneratorDeficiencies,
  scheduleToSetOfTaskDueDates,
  taskHasAllCustomFieldsCompleted,
} from '../../compliance-shared/services/task';
import type { StandardTaskID } from '../../cross-app-shared/types/standard-tasks.ts';
import type {
  AuditLogItem,
  ExternalTaskConcrete,
  NotificationLogItem,
  Schedule,
  StandardTimestamps,
  Task,
  TaskCustomField,
  TaskGenerator,
  TaskTemplate,
  TaskTemplateNew,
  ComplianceFile,
  ComplianceFolder,
  TaskGeneratorDeficiencies,
  ISO8601Timestamp,
  TaskGeneratorNew,
  UidOrRole,
  Role,
  ISO8601DateOnly,
  ComplianceFileExternalNew,
  Method9Values,
  Method9Observations,
  Method9StageBoundary,
  Method9Signature,
  Method9Diagram,
  ComplianceFileFirebaseNew,
  Method9Form,
  ComplianceFolderForUI,
  TaskGeneratorBrief,
  ComplianceFileFirebase,
  Singleton,
  Notification,
  DeterminationComplianceIssueExternalTask,
  PermitIndex,
  PermitIndexID,
  PermitIndexUnitID,
  PermitIndexingExternalTask,
  PermitIndexConditionID,
  ExternalIntegrationConfig,
} from '../../compliance-shared/types/compliance';
import type { Uid } from '../../cross-app-shared/types/common';
import type { FacilitiesAndPermissions, Facility } from '../../compliance-shared/types/common';
import {
  type MaybeRefOrGetter,
  type Ref,
  ref,
  toValue,
  computed,
  type ComputedRef,
  toRaw,
  onScopeDispose,
  watch,
} from 'vue';
import type { ComplianceBackendRawType } from '../../compliance-backend/compliance-backend-raw';
import type { ComplianceBackendTypes } from '../../compliance-backend/compliance-backend';
import {
  onSnapshot as firestoreOnSnapshot,
  query,
  where,
  onSnapshot,
  getDocs,
  doc,
  or,
  and,
  orderBy,
  limit,
  getDoc,
} from 'firebase/firestore';
import { ComplianceDatabase } from '@/firebase';
import { auth, uid } from '../../cross-app-frontend/firebase';
import { snapshotToRef, useDocumentReference, useMultiQueryIn, type UseQueryAsArray, useQueryAsArray } from './utility';
import {
  dateLocal,
  dateNow,
  dateStrDurationAdd,
  duration,
  durationToEnglish,
  timestamp,
  TIME_ZONE_FRONTEND_FALLBACK,
} from '../../cross-app-shared/services/dates-and-durations.ts';
import { determinationComplianceIssueResolve } from './determinations-compliance-issues';
import {
  taskGeneratorExternalTaskIdsOfType,
  taskGeneratorIsDrivenByExternalTask,
} from '../../compliance-shared/services/external-task';
import { confirmModal } from '@/components/modals/ConfirmModal.vue';
import { INTEGRATION_TEST } from '../../cross-app-shared/services/automated-testing.ts';
import { alertModal } from '@/components/modals/AlertModal.vue';
import { toast } from '@/components/util/toast';
import type { StandardTaskBrief } from '../../compliance-shared/types/standard-tasks';
import { standardTaskMapToSortedList } from '@/services/standard-tasks';
import type { PermitIndexCondition, PermitIndexEmissionsUnit } from '../../compliance-shared/types/compliance.ts';
import { permitIndexingBuildUrl } from '@/services/permit-indexing.ts';
import {
  type HazWasteExternalTask,
  hazWasteCompletePrompt,
  hazWastePathOf,
  hazWasteUrl,
} from '@/services/hazwaste-links.ts';
import type { FacilityID } from '../../cross-app-shared/types/db.ts';
import { tenantID } from '@/router/route-constants';
import { facilitiesDetails } from '@/services/facilities.ts';

export type Unsubscribe = () => void;

async function backendRequest<Name extends keyof ComplianceBackendTypes>(
  name: Name,
  args: ComplianceBackendTypes[Name]['request'],
): Promise<ComplianceBackendTypes[Name]['response']> {
  const token = await auth.currentUser?.getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  if (INTEGRATION_TEST) {
    headers['X-Integration-Test'] = 'true';
  }

  const response:
    | { status: 'error'; error: string }
    | { status: 'success'; response: ComplianceBackendTypes[Name]['response'] } = await fetch(
    '/api/compliance/' + name,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(args),
    },
  )
    .then((r) => r.json())
    .catch((e) => {
      console.error(`Backend request to compliance/${name} failed`, args, e);
      throw new Error(
        `Backend request to compliance/${name} failed${e instanceof Error ? ` with error: ${e.message}` : ''}.`,
        { cause: e },
      );
    });

  if (response.status === 'error') {
    console.error(response);
    throw new Error(response.error);
  } else {
    return response.response;
  }
}

async function backendRequestFormData<Name extends keyof ComplianceBackendRawType>(
  name: Name,
  formData: FormData,
): Promise<any> {
  const token = await auth.currentUser?.getIdToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (INTEGRATION_TEST) {
    headers['X-Integration-Test'] = 'true';
  }

  const response: { status: 'error'; error: string } | { status: 'success'; response: any } = await fetch(
    `/api/compliance/${String(name)}`,
    {
      method: 'POST',
      headers,
      body: formData,
    },
  )
    .then((r) => r.json())
    .catch((e) => {
      console.error(`Backend form request to compliance/${name} failed`, Object.fromEntries(formData), e);
      throw new Error(
        `Backend request to compliance/${name} failed${e instanceof Error ? ` with error: ${e.message}` : ''}.`,
        { cause: e },
      );
    });

  if (response.status === 'error') {
    console.error(response);
    throw new Error(response.error);
  } else {
    return response.response;
  }
}

export const actions = {
  task: {
    listenForFacilityTasks: (facilityRef: Ref<Facility['facilityID']>): Ref<Task[] | null> => {
      const oneYearAgo = dateStrDurationAdd(dateLocal(), duration({ months: -3 }));
      return snapshotToRef(facilityRef, null as Task[] | null, (newFacilityID, result) => {
        return firestoreOnSnapshot(
          query(
            ComplianceDatabase.CollectionTask,
            and(
              where('facilityID', '==', newFacilityID),
              or(where('completedAt', '==', null), where('completedAt', '>=', oneYearAgo)),
            ),
          ),
          (snapshots) => {
            result.value = snapshots.docs.map((s) => s.data() as Task).filter((t) => !t.deletedAt);
          },
        );
      });
    },

    listenForOwnedAndInvolvedTasks: (uid: Uid): Ref<Task[] | null> => {
      const oneYearAgo = dateStrDurationAdd(dateLocal(), duration({ months: -3 }));
      return snapshotToRef(ref(null), null as Task[] | null, (_, result) => {
        return firestoreOnSnapshot(
          query(
            ComplianceDatabase.CollectionTask,
            and(
              or(where('owner', '==', uid), where('involved', 'array-contains', uid)),
              or(where('completedAt', '==', null), where('completedAt', '>=', oneYearAgo)),
            ),
          ),
          (snapshots) => {
            result.value = snapshots.docs.map((s) => s.data() as Task).filter((t) => !t.deletedAt);
          },
        );
      });
    },

    async fetchTask(taskID: Task['taskID']): Promise<Task | null> {
      const snapshot = await getDoc(doc(ComplianceDatabase.CollectionTask, taskID));
      return snapshot.data() || null;
    },

    async fetchTaskWithDueAtOrig(
      taskGeneratorID: TaskGenerator['taskGeneratorID'],
      dueAtOrig: ISO8601DateOnly,
    ): Promise<Task | null> {
      const snapshot = await getDocs(
        query(
          ComplianceDatabase.CollectionTask,
          where('taskGeneratorID', '==', taskGeneratorID),
          where('dueAtOrig', '==', dueAtOrig),
          limit(1),
        ),
      );
      return snapshot.docs[0]?.data() || null;
    },

    listenForTask: (taskID: Ref<Task['taskID'] | null>): Ref<Task | null> => {
      return snapshotToRef(taskID, null as Task | null, (newTaskID, result) => {
        if (newTaskID === null) {
          result.value = null;
          return () => {};
        }

        return firestoreOnSnapshot(doc(ComplianceDatabase.CollectionTask, newTaskID), (snapshot) => {
          result.value = (snapshot.data() as Task) || null;
        });
      });
    },

    async fetchActiveAndIncompleteGeneratorTasks(
      taskGeneratorID: MaybeRefOrGetter<TaskGenerator['taskGeneratorID']>,
    ): Promise<Task[]> {
      const taskQuery = query(
        ComplianceDatabase.CollectionTask,
        where('taskGeneratorID', '==', toValue(taskGeneratorID)),
        where('deletedAt', '==', null),
        where('completedAt', '==', null),
      );
      const taskQuerySnapshot = await getDocs(taskQuery);

      return taskQuerySnapshot.docs.map((snapshot) => snapshot.data());
    },

    // This intentionally includes deleted tasks:
    listenForAllGeneratorTasks(taskGeneratorID: Ref<TaskGenerator['taskGeneratorID'] | null>): Ref<Task[] | null> {
      return snapshotToRef(taskGeneratorID, null as null | Task[], (newTaskGeneratorID, result) => {
        if (!newTaskGeneratorID) {
          result.value = null;
          return () => {};
        }

        return firestoreOnSnapshot(
          query(ComplianceDatabase.CollectionTask, where('taskGeneratorID', '==', newTaskGeneratorID)),
          (snapshot) => {
            result.value = snapshot.docs.map((s) => s.data());
          },
        );
      });
    },

    async fetchLatestGeneratorTask(taskGeneratorID: TaskGenerator['taskGeneratorID']): Promise<Task | null> {
      const snapshot = await getDocs(
        query(
          ComplianceDatabase.CollectionTask,
          where('taskGeneratorID', '==', taskGeneratorID),
          orderBy('dueAt', 'desc'),
          limit(1),
        ),
      );

      return snapshot.docs[0]?.data() || null;
    },

    complete: async (task: Task): Promise<void> => {
      const method9ById = await actions.method9.fetchByCustomFieldValues(task.customFieldValues);

      if (!taskHasAllCustomFieldsCompleted(task, method9ById)) {
        await (await import('@/components/tasks/TaskCustomFieldsModal.vue')).taskCustomFieldsModal(task.taskID);
        return;
      }

      const taskGenerator = await actions.generator.fetchByID(task.taskGeneratorID);
      const externalTaskDriving = taskGeneratorIsDrivenByExternalTask(taskGenerator);
      const determinationsComplianceExternalTasks = taskGeneratorExternalTaskIdsOfType(
        taskGenerator,
        'determinationComplianceIssue',
      );

      if (determinationsComplianceExternalTasks.length > 0) {
        const unresolvedExternalTasks = (
          await getDocs(
            query(
              ComplianceDatabase.CollectionExternalTasks,
              where('externalTaskID', 'in', determinationsComplianceExternalTasks),
            ),
          )
        ).docs
          .map((s) => s.data() as DeterminationComplianceIssueExternalTask)
          .filter((t) => !t.issue.isResolved);

        if (unresolvedExternalTasks.length > 0) {
          const action = await confirmModal<'cancel' | 'resolve-only' | 'task-only' | 'both'>({
            title: 'Has the compliance issue been resolved?',
            message: 'Would you like to complete the task, resolve the compliance issue, or both?',
            choices: [
              { label: 'Cancel', value: 'cancel', icon: 'fa-xmark', variant: 'secondary' },
              {
                label: 'Resolve compliance issue',
                value: 'resolve-only',
                icon: 'fa-comment-check',
                variant: 'outline',
              },
              {
                label: 'Complete task',
                value: 'task-only',
                icon: 'fa-memo-circle-check',
                variant: 'outline',
              },
              { label: 'Both', value: 'both', icon: 'fa-check-double', variant: 'outline' },
            ],
            dialogStyle: 'max-width: 600px',
          });

          if (action === 'cancel') {
            return;
          } else if (action === 'resolve-only') {
            await determinationComplianceIssueResolve(unresolvedExternalTasks.map((t) => t.externalTaskID));
            return;
          } else if (action === 'task-only') {
            // Do nothing, falling through will result in the task being completed.
          } else if (action === 'both') {
            const resolved = await determinationComplianceIssueResolve(
              unresolvedExternalTasks.map((t) => t.externalTaskID),
            );
            if (!resolved) {
              return;
            }
            // No return because we want to fall through and complete the task.
          }
        }
      } else if (externalTaskDriving) {
        if (externalTaskDriving.type === 'permitIndexing') {
          const externalTask = (
            await getDoc(doc(ComplianceDatabase.CollectionExternalTasks, externalTaskDriving.externalTaskID))
          ).data() as PermitIndexingExternalTask | undefined;

          const permitIndex = (
            await getDoc(
              doc(ComplianceDatabase.CollectionPermitsIndexing, externalTask?.permitIndexID ?? 'permit_index_unknown'),
            )
          ).data();

          if (externalTask && permitIndex) {
            const hasConditions =
              (
                await getDocs(
                  query(
                    ComplianceDatabase.CollectionPermitsIndexingConditions,
                    where('permitIndexID', '==', permitIndex.permitIndexID),
                    limit(1),
                  ),
                )
              ).docs.length > 0;
            const action = await confirmModal<'complete' | 'open' | 'cancel'>({
              title: `Permit indexing needs to be completed`,
              message:
                `In order to complete this task, the permit must be indexed and all conditions must be recorded.` +
                (hasConditions
                  ? `\n\nBy completing this task, you are confirming that all conditions have been recorded for the permit "${permitIndex.file.name}".`
                  : ''),

              choices: [
                { label: 'Cancel', value: 'cancel', icon: 'fa-xmark', variant: 'outline' },
                {
                  label: 'Open permit',
                  value: 'open',
                  icon: 'fa-arrow-up-right-from-square',
                  variant: 'default',
                },
                ...(hasConditions
                  ? [
                      {
                        label: 'Complete Task',
                        value: 'complete' as const,
                        icon: 'fa-check',
                        variant: 'success' as const,
                      },
                    ]
                  : []),
              ],
            });

            if (action === 'open') {
              window.open(
                permitIndexingBuildUrl({
                  permitIndexID: externalTask.permitIndexID,
                  facilityID: externalTask.facilityID,
                  taskGeneratorID: null,
                }),
                '_blank',
              );
            } else if (action === 'complete') {
              try {
                await actions.permitIndexing.complete(externalTask.permitIndexID);
                await actions.externalTask.permitIndexingSyncEnqueue();
                await alertModal({
                  title: 'Permit has been updated!',
                  message:
                    'The status of this permit has successfully been updated.\n\nPlease wait a few minutes for the Compliance Tool task status to update.',
                });
              } catch (e: any) {
                console.error(e);
                await alertModal({
                  title: 'An error occurred',
                  message: e.message,
                });
              }
            }

            return;
          }
        } else if (
          externalTaskDriving.type === 'hazWasteAccumulationDeadline' ||
          externalTaskDriving.type === 'hazWasteStreamReview' ||
          externalTaskDriving.type === 'hazWasteContainerInspection' ||
          externalTaskDriving.type === 'hazWasteTankInspection' ||
          externalTaskDriving.type === 'hazWasteInspectionFinding'
        ) {
          const externalTask = (
            await getDoc(doc(ComplianceDatabase.CollectionExternalTasks, externalTaskDriving.externalTaskID))
          ).data() as HazWasteExternalTask | undefined;
          const prompt = hazWasteCompletePrompt(externalTaskDriving.type);
          const action = await confirmModal<'open' | 'cancel'>({
            ...prompt,
            choices: [
              { label: 'Cancel', value: 'cancel', icon: 'fa-xmark', variant: 'outline' },
              ...(externalTask
                ? [
                    {
                      label: 'Open in HazWaste',
                      value: 'open' as const,
                      icon: 'fa-arrow-up-right-from-square',
                      variant: 'default' as const,
                    },
                  ]
                : []),
            ],
          });
          if (action === 'open' && externalTask) {
            window.open(hazWasteUrl(hazWastePathOf(externalTask)), '_blank');
          }
          return;
        } else if (externalTaskDriving.type !== null) {
          const unexpected: never = externalTaskDriving.type;
          console.error('Got unexpected externalTaskDriving.type:', unexpected);
        }
      }

      const completionResult = await (
        await import('@/components/tasks/TaskCompletionModal.vue')
      ).taskCompletionModalOpen(task.taskID);
      if (!completionResult) {
        return;
      }

      const { completedAt, dueAtNext, correctiveActions } = completionResult;

      return backendRequest('taskComplete', {
        tenantID,
        taskID: task.taskID,
        completedAt,
        dueAtNext,
        correctiveActions,
      });
    },

    async uncomplete(task: Task): Promise<void> {
      return backendRequest('taskUncomplete', { tenantID, taskID: task.taskID });
    },

    ownerUpdate(args: { taskID: Task['taskID']; ownerNew: UidOrRole; updateTaskSeries: boolean }): Promise<void> {
      return backendRequest('taskOwnerUpdate', { ...args, tenantID });
    },

    involvedUpdate(args: { taskID: Task['taskID']; involved: UidOrRole[]; updateTaskSeries: boolean }): Promise<void> {
      return backendRequest('taskInvolvedUpdate', { ...args, tenantID });
    },

    commentAdd(args: {
      taskID: Task['taskID'];
      content: string;
      replyingTo: Task['comments'][number]['commentID'] | null;
    }) {
      return backendRequest('taskCommentAdd', { ...args, tenantID });
    },

    notesUpdate(args: { taskID: Task['taskID']; notes: string }): Promise<void> {
      return backendRequest('taskNotesUpdate', { ...args, tenantID });
    },

    customFieldsUpdate(args: {
      taskID: Task['taskID'];
      customFieldValues: Record<TaskCustomField['fieldID'], any>;
    }): Promise<void> {
      return backendRequest('taskCustomFieldsUpdate', { ...args, tenantID });
    },

    correctiveActionsUpdateForField(
      taskID: Task['taskID'],
      customFieldID: TaskCustomField['fieldID'],
      flagged: boolean,
    ): Promise<void> {
      return backendRequest('correctiveActionsUpdateForField', { tenantID, taskID, customFieldID, flagged });
    },

    updateNeedsCorrectiveAction(taskID: Task['taskID'], flagged: boolean): Promise<void> {
      return backendRequest('updateNeedsCorrectiveAction', { tenantID, taskID, flagged });
    },

    async delete({ taskID }: Pick<Task, 'taskID' | 'taskGeneratorID'>): Promise<void> {
      return backendRequest('taskDelete', { tenantID, taskID });
    },

    undelete(taskID: Task['taskID']): Promise<void> {
      return backendRequest('taskUndelete', { tenantID, taskID });
    },

    async dueDateChange(task: Task, dueAt: ISO8601DateOnly): Promise<void> {
      return backendRequest('taskDueDateChange', { tenantID, taskID: task.taskID, dueAt });
    },

    filesUpdate(taskID: Task['taskID'], files: ComplianceFile['fileID'][]): Promise<void> {
      return backendRequest('taskFilesUpdate', { tenantID, taskID, files });
    },

    listenToNotifications(taskID: MaybeRefOrGetter<Task['taskID']>) {
      const { documents } = useQueryAsArray(
        computed(function () {
          return query(ComplianceDatabase.CollectionTaskNotification, where('taskID', '==', toValue(taskID)));
        }),
      );

      return computed(function () {
        const taskNotifications: Record<ISO8601DateOnly, Notification> = {};

        for (const taskNotification of documents.value) {
          taskNotifications[taskNotification.date] = taskNotification;
        }

        return taskNotifications;
      });
    },
  },

  generator: {
    async fetchByID(taskGeneratorID: TaskGenerator['taskGeneratorID']): Promise<TaskGenerator | null> {
      const snapshot = await getDoc(doc(ComplianceDatabase.CollectionTaskGenerator, taskGeneratorID));
      return snapshot.data() || null;
    },

    listenForDeficiencies(
      taskGenerators: MaybeRefOrGetter<Partial<Record<TaskGenerator['taskGeneratorID'], TaskGenerator>>>,
    ): ComputedRef<TaskGeneratorDeficiencies[]> {
      const standardTaskTemplates = useQueryAsArray(
        query(ComplianceDatabase.CollectionTaskTemplate, where('isStandardTaskTemplate', '==', true)),
        true,
      ).documents;
      const taskTemplatesByStandardTaskID = computed(function () {
        const taskTemplatesByStandardTaskID = new Map<StandardTaskID, TaskTemplate[]>();

        for (const taskTemplate of toValue(standardTaskTemplates)) {
          for (const standardTaskID of taskTemplate.standardTaskIds) {
            if (taskTemplatesByStandardTaskID.has(standardTaskID)) {
              taskTemplatesByStandardTaskID.get(standardTaskID)!.push(taskTemplate);
            } else {
              taskTemplatesByStandardTaskID.set(standardTaskID, [taskTemplate]);
            }
          }
        }

        return taskTemplatesByStandardTaskID;
      });

      return computed(function () {
        const deficientTasks: TaskGeneratorDeficiencies[] = [];

        for (const taskGenerator of Object.values(toValue(taskGenerators))) {
          if (taskGenerator) {
            const applicableTaskTemplates: TaskTemplate[] = [];

            for (const standardTaskID of taskGenerator.standardTaskIds) {
              for (const taskTemplate of toValue(taskTemplatesByStandardTaskID).get(standardTaskID) ?? []) {
                applicableTaskTemplates.push(toRaw(taskTemplate));
              }
            }

            const deficiencies = getGeneratorDeficiencies(toRaw(taskGenerator), applicableTaskTemplates);

            if (deficiencies.length > 0) {
              deficientTasks.push({
                taskGenerator: toRaw(taskGenerator),
                deficiencies,
              });
            }
          }
        }

        return deficientTasks;
      });
    },

    listenForDeficienciesSingleGenerator(
      taskGeneratorID: Ref<TaskGenerator['taskGeneratorID']>,
    ): ComputedRef<TaskGeneratorDeficiencies | null> {
      const taskGenerator = actions.generator.listenForGenerator(taskGeneratorID);

      const applicableTaskTemplates = useQueryAsArray(
        computed(() => {
          if (!taskGenerator.value || taskGenerator.value.standardTaskIds.length === 0) {
            return null;
          }

          return query(
            ComplianceDatabase.CollectionTaskTemplate,
            where('isStandardTaskTemplate', '==', true),
            where('standardTaskIds', 'array-contains-any', taskGenerator.value.standardTaskIds),
          );
        }),
      ).documents;

      return computed((): TaskGeneratorDeficiencies | null => {
        if (taskGenerator.value && applicableTaskTemplates.value.length > 0) {
          return {
            taskGenerator: taskGenerator.value,
            deficiencies: getGeneratorDeficiencies(toRaw(taskGenerator.value), applicableTaskTemplates.value),
          };
        } else {
          return null;
        }
      });
    },

    listenForGenerator: (taskGeneratorID: Ref<TaskGenerator['taskGeneratorID'] | null>): Ref<TaskGenerator | null> => {
      return snapshotToRef(taskGeneratorID, null as TaskGenerator | null, (newTaskGeneratorID, result) => {
        if (!newTaskGeneratorID) {
          result.value = null;
          return () => {};
        }

        return firestoreOnSnapshot(doc(ComplianceDatabase.CollectionTaskGenerator, newTaskGeneratorID), (snapshot) => {
          result.value = (snapshot.data() as TaskGenerator) || null;
        });
      });
    },

    listenForCorrectiveAction: (
      taskID: Ref<Task['taskID']>,
      taskGeneratorID: Ref<TaskGenerator['taskGeneratorID']>,
      customFieldID: Ref<TaskCustomField['fieldID'] | null>,
    ): Ref<TaskGenerator | null> => {
      return snapshotToRef(
        computed(() => [taskID.value, taskGeneratorID.value, customFieldID.value] as const),
        null as TaskGenerator | null,
        ([newTaskID, newTaskGeneratorID, newCustomFieldID], result) => {
          return firestoreOnSnapshot(
            query(
              ComplianceDatabase.CollectionTaskGenerator,
              where('correctiveActionFor.taskGeneratorID', '==', newTaskGeneratorID),
              where('correctiveActionFor.taskID', '==', newTaskID),
              newCustomFieldID
                ? where('correctiveActionFor.customFields', 'array-contains', newCustomFieldID)
                : where('correctiveActionFor.customFields', '==', []),
              limit(1),
            ),
            (snapshot) => {
              result.value = snapshot.docs[0]?.data() || null;
            },
          );
        },
      );
    },

    listenForFacilityGenerators(
      facilityID: Ref<Facility['facilityID']>,
      mode: Ref<'active' | 'deleted'>,
    ): Ref<TaskGenerator[] | null> {
      return snapshotToRef<[Facility['facilityID'], 'active' | 'deleted'], TaskGenerator[] | null>(
        [facilityID, mode],
        null,
        ([facilityID, mode], result) => {
          return firestoreOnSnapshot(
            query(
              ComplianceDatabase.CollectionTaskGenerator,
              where('facilityID', '==', facilityID),
              mode === 'deleted' ? where('deletedAt', '!=', null) : where('deletedAt', '==', null),
            ),
            (snapshots) => {
              result.value = snapshots.docs.map((s) => s.data());
            },
          );
        },
      );
    },

    listenForFacilityGeneratorsMulti(
      facilitiesViewing: Ref<Facility['facilityID'][]>,
      mode: MaybeRefOrGetter<'active' | 'deleted'>,
    ): UseQueryAsArray<TaskGenerator> {
      return useMultiQueryIn(
        computed(function () {
          return query(
            ComplianceDatabase.CollectionTaskGenerator,
            where('deletedAt', toValue(mode) === 'active' ? '==' : '!=', null),
          );
        }),
        'facilityID',
        facilitiesViewing,
      );
    },

    listenForInvolved(involvedUID: MaybeRefOrGetter<Uid | null>) {
      return useQueryAsArray(
        computed(function () {
          const involvedUIDValue = toValue(involvedUID);

          if (involvedUIDValue === null) {
            return null;
          }

          return query(
            ComplianceDatabase.CollectionTaskGenerator,
            where('involvedConfigured', 'array-contains', involvedUIDValue),
            where('deletedAt', '==', null),
          );
        }),
      );
    },

    async fetchFacilityGeneratorsMulti(facilitiesViewing: Facility['facilityID'][]): Promise<TaskGenerator[]> {
      const promises: Promise<void>[] = [];
      const result: TaskGenerator[] = [];
      const chunkSize = 10;

      for (let i = 0; i < facilitiesViewing.length; i += chunkSize) {
        const chunk = facilitiesViewing.slice(i, i + chunkSize);
        promises.push(
          getDocs(query(ComplianceDatabase.CollectionTaskGenerator, where('facilityID', 'in', chunk))).then(
            (snapshot) => {
              result.push(...snapshot.docs.map((s) => s.data()));
            },
          ),
        );
      }

      await Promise.all(promises);
      return result;
    },

    useMyActiveGeneratorsById(): {
      isLoading: Ref<boolean>;
      documents: Ref<Record<TaskGenerator['taskGeneratorID'], TaskGenerator>>;
    } {
      const isLoading = ref(true);
      const documents = snapshotToRef(
        ref(null),
        {} as Record<TaskGenerator['taskGeneratorID'], TaskGenerator>,
        (_, result) => {
          return firestoreOnSnapshot(
            query(
              ComplianceDatabase.CollectionTaskGenerator,
              and(
                where('deletedAt', '==', null),
                or(where('owner', '==', uid), where('involved', 'array-contains', uid)),
              ),
            ),
            (snapshots) => {
              const newResult: Record<TaskGenerator['taskGeneratorID'], TaskGenerator> = {};
              for (const snapshot of snapshots.docs) {
                const generator = snapshot.data();
                newResult[generator.taskGeneratorID] = generator;
              }
              result.value = newResult;
              isLoading.value = false;
            },
          );
        },
      );
      return { isLoading, documents };
    },

    listenForMultipleFacilityActiveGenerators(
      facilitiesViewing: Ref<Facility['facilityID'][]>,
    ): UseQueryAsArray<TaskGenerator> {
      return useMultiQueryIn(
        query(ComplianceDatabase.CollectionTaskGenerator, where('deletedAt', '==', null)),
        'facilityID',
        facilitiesViewing,
      );
    },

    listenForOwnedActiveGenerators: (uid: Uid, onSnapshot: (generators: TaskGenerator[]) => any): Unsubscribe => {
      return firestoreOnSnapshot(
        query(ComplianceDatabase.CollectionTaskGenerator, where('owner', '==', uid), where('deletedAt', '==', null)),
        (snapshots) => {
          onSnapshot(snapshots.docs.map((s) => s.data() as TaskGenerator));
        },
      );
    },

    async createFromScratch(generator: TaskGeneratorNew): Promise<TaskGenerator> {
      return (
        await backendRequest('taskGeneratorCreate', {
          taskGenerator: generator,
        })
      ).taskGenerator;
    },

    async createManyFromScratch(
      taskGenerators: {
        generator: TaskGeneratorNew;
        importAsDeleted: boolean;
        tasksCompletedAt: Record<ISO8601DateOnly, ISO8601Timestamp>;
      }[],
    ): Promise<void> {
      return backendRequest('taskGeneratorCreateMany', { taskGenerators });
    },

    async updateTaskGeneratorAndActiveTasks(
      oldTaskGeneratorRef: MaybeRefOrGetter<TaskGenerator>,
      newTaskGeneratorRef: MaybeRefOrGetter<TaskGenerator>,
      roles: MaybeRefOrGetter<Role[] | null>,
      tasksToSendToGraveyardRef: MaybeRefOrGetter<Task['taskID'][]>,
      useModal = true,
    ): Promise<TaskGenerator | null> {
      const oldTaskGenerator = toValue(oldTaskGeneratorRef);
      const newTaskGenerator = toValue(newTaskGeneratorRef);
      const tasks = await actions.task.fetchActiveAndIncompleteGeneratorTasks(newTaskGenerator.taskGeneratorID);

      if (
        jsonStableStringify(oldTaskGenerator.schedule.frequency) !==
          jsonStableStringify(newTaskGenerator.schedule.frequency) &&
        // In theory it should never find anything to create, but I want to make it clear that this is (and should be)
        // the case for these types of tasks:
        !newTaskGenerator.schedule.generateOnlyOnCompletion
      ) {
        let tasksToCreateDueDates = Array.from(
          scheduleToSetOfTaskDueDates(
            newTaskGenerator.schedule,
            newTaskGenerator.dontGenerateTasksBefore,
            new Set(newTaskGenerator.dueDatesExcluded ?? []),
            dateNow(),
            facilitiesDetails[newTaskGenerator.facilityID]?.timeZone ?? TIME_ZONE_FRONTEND_FALLBACK,
            newTaskGenerator.dueDateAdjustments ?? {},
          ).dueDates,
        ).sort();
        const latest = await actions.task.fetchLatestGeneratorTask(newTaskGenerator.taskGeneratorID);
        if (latest) {
          tasksToCreateDueDates = tasksToCreateDueDates.filter((dueAt) => dueAt > latest.dueAt);
        }

        if (tasksToCreateDueDates.length > 10) {
          if (
            !(await confirmModal({
              title: `This will create ${tasksToCreateDueDates.length} tasks`,
              message: `The schedule has been changed from creating new tasks every ${durationToEnglish(
                oldTaskGenerator.schedule.create,
                true,
              )}, to creating new tasks ${durationToEnglish(
                newTaskGenerator.schedule.create,
                true,
              )}.\n\nThis will cause ${
                tasksToCreateDueDates.length
              } tasks to be created.\n\nAre you sure you want to proceed?`,
            }))
          ) {
            return null;
          }
        }
      }

      let tasksToSendToGraveyard = toValue(tasksToSendToGraveyardRef);
      if (tasksToSendToGraveyard.length > 0) {
        const result = await confirmModal<'cancel' | 'delete' | 'keep'>({
          title: `This will delete tasks`,
          message: `The schedule has been changed, meaning that ${tasksToSendToGraveyard.length} task(s) have already been created which are no longer needed.\n\nAre you sure you want to delete them?`,
          choices: [
            { label: 'Cancel', value: 'cancel', icon: 'fa-xmark', variant: 'outline' },
            { label: 'Keep all tasks', value: 'keep', icon: 'fa-check', variant: 'default' },
            {
              label: `Yes, delete ${tasksToSendToGraveyard.length} task(s)`,
              value: 'delete',
              icon: 'fa-trash',
              variant: 'destructive',
            },
          ],
        });

        if (result === 'cancel') {
          return null;
        } else if (result === 'keep') {
          tasksToSendToGraveyard = [];
        }
      }

      const fieldsToPush: string[] = [];

      if (oldTaskGenerator.title !== newTaskGenerator.title) {
        fieldsToPush.push('Title');
      }

      if (oldTaskGenerator.description !== newTaskGenerator.description) {
        fieldsToPush.push('Description');
      }

      if (
        jsonStableStringify(oldTaskGenerator.standardTaskIds.slice().sort()) !==
        jsonStableStringify(newTaskGenerator.standardTaskIds.slice().sort())
      ) {
        fieldsToPush.push('Standard task IDs');
      }

      if (
        jsonStableStringify(oldTaskGenerator.customFieldDefinitions) !==
        jsonStableStringify(newTaskGenerator.customFieldDefinitions)
      ) {
        fieldsToPush.push('Custom fields');
      }

      if (oldTaskGenerator.ownerConfigured !== newTaskGenerator.ownerConfigured) {
        fieldsToPush.push('Owner');
      }

      if (
        jsonStableStringify(oldTaskGenerator.involvedConfigured.slice().sort()) !==
        jsonStableStringify(newTaskGenerator.involvedConfigured.slice().sort())
      ) {
        fieldsToPush.push('Involved');
      }

      if (tasks.length > 0 && fieldsToPush.length > 0) {
        if (useModal) {
          const { update, tasksIncluded } = await taskGeneratorUpdateModal(
            newTaskGenerator,
            fieldsToPush,
            toValue(roles),
            tasks,
          );
          if (update) {
            const tasksToUpdate: Task['taskID'][] = [];

            for (const { task, include } of tasksIncluded) {
              if (include) {
                tasksToUpdate.push(task.taskID);
              }
            }

            return await actions.generator.updateTaskGenerator(
              newTaskGenerator as TaskGenerator,
              tasksToUpdate,
              tasksToSendToGraveyard,
            );
          } else {
            return null;
          }
        } else {
          return await actions.generator.updateTaskGenerator(
            newTaskGenerator as TaskGenerator,
            tasks.map((t) => t.taskID),
            tasksToSendToGraveyard,
          );
        }
      } else {
        return await actions.generator.updateTaskGenerator(newTaskGenerator, [], tasksToSendToGraveyard);
      }
    },

    updateTaskGenerator(
      taskGenerator: TaskGenerator,
      tasksToUpdate: Task['taskID'][],
      tasksToSendToGraveyard: Task['taskID'][],
    ): Promise<TaskGenerator> {
      return backendRequest('taskGeneratorUpdate', { tenantID, taskGenerator, tasksToUpdate, tasksToSendToGraveyard });
    },

    async taskGeneratorPullChangesWithModal(
      taskGenerator: MaybeRefOrGetter<TaskGenerator>,
      taskTemplateID: MaybeRefOrGetter<TaskTemplate['taskTemplateID']>,
      forceUpdate: boolean = false,
    ): Promise<TaskGenerator | null> {
      let update = false;
      let updateTasks = false;

      if (!forceUpdate) {
        ({ update, updateTasks } = await taskGeneratorPullChangesModal());
      }

      if (update) {
        return actions.generator.taskGeneratorPullChanges(
          toValue(taskGenerator).taskGeneratorID,
          toValue(taskTemplateID),
          updateTasks,
        );
      } else {
        return null;
      }
    },

    taskGeneratorPullChanges(
      taskGeneratorID: TaskGenerator['taskGeneratorID'],
      taskTemplateID: TaskTemplate['taskTemplateID'],
      updateTasks: boolean,
    ): Promise<TaskGenerator> {
      return backendRequest('taskGeneratorPullChanges', { tenantID, taskGeneratorID, taskTemplateID, updateTasks });
    },

    async taskGeneratorDeleteWithModal(
      taskGeneratorRef: MaybeRefOrGetter<TaskGenerator>,
      roles: MaybeRefOrGetter<Role[] | null>,
    ): Promise<boolean> {
      const taskGenerator = toValue(taskGeneratorRef);
      const tasks = await actions.task.fetchActiveAndIncompleteGeneratorTasks(taskGenerator.taskGeneratorID);

      const confirmData = await taskGeneratorDeleteModal(taskGenerator, tasks, toValue(roles));

      if (confirmData.delete) {
        const tasksToDelete: Task['taskID'][] = [];

        for (const { task, include } of confirmData.tasksIncluded) {
          if (include) {
            tasksToDelete.push(task.taskID);
          }
        }

        await actions.generator.taskGeneratorDelete(taskGenerator.taskGeneratorID, tasksToDelete);
        return true;
      } else {
        return false;
      }
    },

    taskGeneratorDelete(
      taskGeneratorID: TaskGenerator['taskGeneratorID'],
      tasksToUpdate: Task['taskID'][],
    ): Promise<void> {
      return backendRequest('taskGeneratorDelete', { tenantID, taskGeneratorID, tasksToUpdate });
    },

    dueDateChange({
      taskGeneratorID,
      dueAtOrig,
      dueAtNew,
    }: {
      taskGeneratorID: TaskGenerator['taskGeneratorID'];
      dueAtOrig: ISO8601DateOnly;
      dueAtNew: ISO8601DateOnly;
    }): Promise<void> {
      return backendRequest('taskGeneratorDueDateChange', { tenantID, taskGeneratorID, dueAtOrig, dueAtNew });
    },

    addFileCustomField({
      taskGeneratorID,
      taskID,
      files,
      label,
    }: {
      taskGeneratorID: TaskGenerator['taskGeneratorID'];
      taskID: Task['taskID'];
      files: ComplianceFile['fileID'][];
      label: string;
    }): Promise<void> {
      return backendRequest('taskGeneratorAddFileCustomField', { tenantID, taskGeneratorID, taskID, files, label });
    },

    futureTaskDelete(taskGeneratorID: TaskGenerator['taskGeneratorID'], dueAt: ISO8601DateOnly): Promise<void> {
      return backendRequest('taskGeneratorFutureTaskDelete', { tenantID, taskGeneratorID, dueAt });
    },

    futureTaskRestore(taskGeneratorID: TaskGenerator['taskGeneratorID'], dueAt: ISO8601DateOnly): Promise<void> {
      return backendRequest('taskGeneratorFutureTaskRestore', { tenantID, taskGeneratorID, dueAt });
    },

    lockedTasksPushDueDates() {
      return backendRequest('taskGeneratorsLockedTasksPushDueDates', { tenantID });
    },
  },

  template: {
    async fetchByID(taskTemplateID: TaskTemplate['taskTemplateID']): Promise<TaskTemplate | null> {
      return (await getDoc(doc(ComplianceDatabase.CollectionTaskTemplate, taskTemplateID))).data() ?? null;
    },

    useActiveTemplates(): Ref<TaskTemplate[] | null> {
      return snapshotToRef(ref(null), null as TaskTemplate[] | null, (_, result) => {
        return firestoreOnSnapshot(
          query(ComplianceDatabase.CollectionTaskTemplate, where('deletedAt', '==', null)),
          (snapshot) => {
            result.value = snapshot.docs.map((s) => s.data());
          },
        );
      });
    },

    useTaskTemplate(taskTemplateID: Ref<TaskTemplate['taskTemplateID'] | null>): Ref<TaskTemplate | null> {
      return snapshotToRef(taskTemplateID, null as TaskTemplate | null, (value, result) => {
        if (!value) {
          result.value = null;
          return () => {};
        }

        return firestoreOnSnapshot(doc(ComplianceDatabase.CollectionTaskTemplate, value), (snapshot) => {
          result.value = snapshot.data() || null;
        });
      });
    },

    createFromScratch(template: TaskTemplateNew): Promise<TaskTemplate> {
      return backendRequest('taskTemplateCreate', { tenantID, taskTemplate: template });
    },

    updateTaskTemplate(
      taskTemplate: TaskTemplate,
      updateGenerators: boolean,
      updateTasks: boolean,
    ): Promise<TaskTemplate> {
      return backendRequest('updateTaskTemplate', { tenantID, taskTemplate, updateGenerators, updateTasks });
    },

    deleteTaskTemplate(taskTemplateID: TaskTemplate['taskTemplateID']): Promise<void> {
      return backendRequest('deleteTaskTemplate', { tenantID, taskTemplateID });
    },

    taskTemplateCreateTaskGenerator(args: {
      taskTemplateID: TaskTemplate['taskTemplateID'];
      facilityID: Facility['facilityID'];
      title: string;
      description: string;
      schedule: Schedule;
      ownerConfigured: UidOrRole;
      involvedConfigured: UidOrRole[];
      externalTaskID?: ExternalTaskConcrete['externalTaskID'];
    }): Promise<{ taskGenerator: TaskGenerator; tasks: Task[] }> {
      return backendRequest('taskTemplateCreateTaskGenerator', { ...args, tenantID });
    },
  },

  standardTask: {
    async list(): Promise<StandardTaskBrief[]> {
      return standardTaskMapToSortedList(
        (await getDoc(ComplianceDatabase.CollectionSingleton.standardTasks)).data() ?? {},
      );
    },

    listen(): Ref<StandardTaskBrief[]> {
      return snapshotToRef(ref(null), [] as StandardTaskBrief[], (_, result) => {
        return firestoreOnSnapshot(ComplianceDatabase.CollectionSingleton.standardTasks, (snapshot) => {
          result.value = standardTaskMapToSortedList(snapshot.data() ?? {});
        });
      });
    },
  },

  auditLog: {
    listen(
      queryRef: Ref<
        Partial<Pick<AuditLogItem, 'type' | 'taskID' | 'taskGeneratorID' | 'taskTemplateID' | 'userID' | 'facilityID'>>
      >,
    ): Ref<AuditLogItem[]> {
      return snapshotToRef(queryRef, [] as AuditLogItem[], (newQuery, result) => {
        const filters = Object.entries(newQuery).map(([key, value]) => where(key, '==', value));
        if (filters.length === 0) {
          result.value = [];
          return () => {};
        }

        return firestoreOnSnapshot(query(ComplianceDatabase.CollectionAuditLogs, ...filters), (snapshots) => {
          result.value = snapshots.docs.map((s) => s.data() as AuditLogItem);
        });
      });
    },
  },

  notifications: {
    listen(
      queryRef: Ref<
        Partial<
          Pick<NotificationLogItem, 'actionType' | 'taskID' | 'taskGeneratorID' | 'taskTemplateID' | 'facilityID'>
        >
      >,
    ): Ref<NotificationLogItem[]> {
      return snapshotToRef(queryRef, [] as NotificationLogItem[], (newQuery, result) => {
        const filters = Object.entries(newQuery).map(([key, value]) => where(key, '==', value));
        if (filters.length === 0) {
          result.value = [];
          return () => {};
        }

        return firestoreOnSnapshot(query(ComplianceDatabase.CollectionNotificationLogs, ...filters), (snapshots) => {
          result.value = snapshots.docs.map((s) => s.data() as NotificationLogItem);
        });
      });
    },
  },

  externalTask: {
    async fetchById(externalTaskID: ExternalTaskConcrete['externalTaskID']): Promise<ExternalTaskConcrete | null> {
      return (await getDoc(doc(ComplianceDatabase.CollectionExternalTasks, externalTaskID))).data() ?? null;
    },

    listenById(ref: Ref<ExternalTaskConcrete['externalTaskID'] | null>): Ref<ExternalTaskConcrete | null> {
      return snapshotToRef(ref, null as ExternalTaskConcrete | null, (newExternalTaskID, result) => {
        if (!newExternalTaskID) {
          result.value = null;
          return () => {};
        }

        return firestoreOnSnapshot(doc(ComplianceDatabase.CollectionExternalTasks, newExternalTaskID), (snapshot) => {
          result.value = snapshot.data() ?? null;
        });
      });
    },

    listenByGeneratorId(ref: Ref<TaskGenerator['taskGeneratorID']>): Ref<ExternalTaskConcrete[] | null> {
      return snapshotToRef(ref, null as null | ExternalTaskConcrete[], (newTaskGeneratorID, result) => {
        return firestoreOnSnapshot(
          query(
            ComplianceDatabase.CollectionExternalTasks,
            where('importedAs.taskGeneratorID', '==', newTaskGeneratorID),
          ),
          (snapshot) => {
            result.value = snapshot.docs.map((s) => s.data());
          },
        );
      });
    },

    listenForSuggestedTasks(ref: Ref<Facility['facilityID'] | null>): Ref<ExternalTaskConcrete[] | null> {
      return snapshotToRef(ref, null as ExternalTaskConcrete[] | null, (newFacilityID, result) => {
        if (!newFacilityID) {
          result.value = null;
          return () => {};
        }

        return firestoreOnSnapshot(
          query(
            ComplianceDatabase.CollectionExternalTasks,
            where('facilityID', '==', newFacilityID),
            where('importedAs', '==', null),
          ),
          (snapshot) => {
            result.value = snapshot.docs.map((s) => s.data());
          },
        );
      });
    },

    listenForSuggestedTaskCount(facilities: MaybeRefOrGetter<Facility['facilityID'][]>): ComputedRef<number> {
      const suggestedTaskCounts = ref<Singleton.SuggestedTaskCounts | null>(null);

      onSnapshot(ComplianceDatabase.CollectionSingleton.suggestedTaskCounts, function (suggestedTaskCountsSnapshot) {
        suggestedTaskCounts.value = suggestedTaskCountsSnapshot.data() ?? null;
      });

      return computed(function () {
        let count = 0;

        if (suggestedTaskCounts.value) {
          for (const facilityID of toValue(facilities)) {
            count += suggestedTaskCounts.value.suggestedTaskCounts[facilityID] ?? 0;
          }
        }

        return count;
      });
    },

    listenForSuggestedTasksForViewedFacilities(
      facilities: Ref<Facility['facilityID'][]>,
    ): ComputedRef<ExternalTaskConcrete[] | null> {
      const { documents, isLoading } = useMultiQueryIn(
        query(ComplianceDatabase.CollectionExternalTasks, where('importedAs', '==', null)),
        'facilityID',
        facilities,
      );

      return computed(function () {
        return isLoading.value ? null : documents.value;
      });
    },

    markNotApplicable(args: { externalTaskID: ExternalTaskConcrete['externalTaskID']; reason: string }): Promise<void> {
      return backendRequest('externalTaskMarkNotApplicable', { ...args, tenantID });
    },

    markApplicable(args: { externalTaskID: ExternalTaskConcrete['externalTaskID'] }): Promise<void> {
      return backendRequest('externalTaskMarkApplicable', { ...args, tenantID });
    },

    assignTaskGenerator(
      externalTaskID: ExternalTaskConcrete['externalTaskID'],
      taskGeneratorID: TaskGenerator['taskGeneratorID'],
    ): Promise<void> {
      return backendRequest('externalTaskAssignTaskGenerator', { tenantID, taskGeneratorID, externalTaskID });
    },

    unassignTaskGenerator(
      externalTaskID: ExternalTaskConcrete['externalTaskID'],
      taskGeneratorID: TaskGenerator['taskGeneratorID'],
    ): Promise<void> {
      return backendRequest('externalTaskUnassignTaskGenerator', { tenantID, taskGeneratorID, externalTaskID });
    },

    determinationComplianceIssueResolve(
      externalTaskID: ExternalTaskConcrete['externalTaskID'],
      explanation: string,
    ): Promise<void> {
      return backendRequest('determinationComplianceIssueResolve', { tenantID, externalTaskID, explanation });
    },

    permitIndexingSyncEnqueue(): Promise<void> {
      return backendRequest('permitIndexingSyncEnqueue', { tenantID });
    },

    determinationsComplianceSyncEnqueue(): Promise<void> {
      return backendRequest('determinationsComplianceSyncEnqueue', { tenantID });
    },

    determinationsApplicableTasksEnqueue(): Promise<void> {
      return backendRequest('determinationsApplicableTasksEnqueue', { tenantID });
    },
  },

  backend: {
    async enqueueAndProcessStandardBatchJobs(): Promise<void> {
      await backendRequest('enqueueStandardBatchJobs', { tenantID });
    },

    async devImportData(): Promise<void> {
      if (tenantID) {
        await backendRequest('devImportData', { tenantID, isIntegrationTest: INTEGRATION_TEST });
      }
    },

    devScheduleAllTemplates(): Promise<void> {
      const hiserJoyFacilityID: Facility['facilityID'] = 1 as FacilityID;

      return backendRequest('devScheduleAllTemplates', { facilityForTasks: hiserJoyFacilityID });
    },

    suggestedTasksUpdateCount(): Promise<void> {
      return backendRequest('suggestedTasksUpdateCount', { tenantID });
    },

    notificationsSyncAndSend(): Promise<void> {
      return backendRequest('notificationsSyncAndSend', { tenantID });
    },

    backfillTaskNumbers() {
      return backendRequest('backfillTaskNumbers', { tenantID });
    },

    dailyDigestForceSendIfNeeded() {
      return backendRequest('dailyDigestForceSendIfNeeded', { tenantID });
    },

    taskGeneratorsFollowUsersEnqueue() {
      return backendRequest('taskGeneratorsFollowUsersEnqueue', { tenantID });
    },
  },

  file: {
    createExternal(fileData: ComplianceFileExternalNew): Promise<ComplianceFile> {
      return backendRequest('fileCreateExternal', { tenantID, fileData });
    },

    createFirebase(file: File, fileData: ComplianceFileFirebaseNew): Promise<ComplianceFileFirebase> {
      if (file.size >= 1024 * 1024 * 32) {
        throw new Error(`The file "${file.name}" is too large. Only files smaller than 32MB may be uploaded.`);
      }

      const formData = new FormData();

      formData.append('file', file);
      formData.append('fileData', JSON.stringify(fileData));

      return backendRequestFormData('fileCreateFirebase', formData);
    },

    async get(fileID: ComplianceFile['fileID']): Promise<ComplianceFile | null> {
      const file = (await getDoc(doc(ComplianceDatabase.CollectionFiles, fileID))).data();
      if (file?.deletedAt) {
        return null;
      }

      return file || null;
    },
  },

  folder: {
    counts(folderID: ComplianceFolder['folderID']): Promise<{ folders: number; tasks: number }> {
      return backendRequest('folderCounts', { tenantID, folderID });
    },

    list(facilities: Ref<Facility['facilityID'][]>): Ref<ComplianceFolderForUI[]> {
      return useMultiQueryIn(
        query(ComplianceDatabase.CollectionFolders, where('deletedAt', '==', null)),
        'facilityID',
        facilities,
      ).documents;
    },

    create(
      name: string,
      facilityID: Facility['facilityID'],
      parentFolderID: ComplianceFolder['folderID'] | null,
    ): Promise<ComplianceFolder> {
      return backendRequest('folderCreate', {
        name,
        facilityID,
        parentFolderIDs: parentFolderID ? [parentFolderID] : [],
      });
    },

    rename(folderID: ComplianceFolder['folderID'], newName: string): Promise<void> {
      return backendRequest('folderRename', {
        tenantID,
        folderID,
        name: newName,
      });
    },

    move(
      folderID: ComplianceFolder['folderID'],
      sourceFolderID: ComplianceFolder['folderID'] | null,
      destinationFolderID: ComplianceFolder['folderID'] | null,
    ): Promise<ComplianceFolder> {
      return backendRequest('folderMove', {
        tenantID,
        folderID,
        sourceFolderID,
        destinationFolderID,
      });
    },

    delete(folderID: ComplianceFolder['folderID']): Promise<void> {
      return backendRequest('folderDelete', { tenantID, folderID });
    },

    tasksByFolder(
      facilityIDRef: Ref<Facility['facilityID'] | null>,
      folderIDRef: Ref<ComplianceFolder['folderID']>,
    ): Ref<null | {
      facilityID: Facility['facilityID'] | null;
      folderID: ComplianceFolder['folderID'];
      tasks: Task[];
    }> {
      return snapshotToRef<
        [Facility['facilityID'] | null, ComplianceFolder['folderID']],
        null | { facilityID: Facility['facilityID'] | null; folderID: ComplianceFolder['folderID']; tasks: Task[] }
      >([facilityIDRef, folderIDRef], null, ([facilityID, folderID], result) => {
        if (
          facilityID === null ||
          folderID === 'folder_root' ||
          folderID === 'folder_noexist' ||
          folderID === 'folder_standard_tasks' ||
          folderID.startsWith('folder_standard_task_') ||
          !folderID.startsWith('folder_generator_')
        ) {
          setTimeout(() => (result.value = { facilityID, folderID, tasks: [] }), 1);
          return () => {};
        } else if (folderID.startsWith('folder_generator_')) {
          return firestoreOnSnapshot(
            query(
              ComplianceDatabase.CollectionTask,
              where('taskGeneratorID', '==', folderID.replace(/^folder_/, '')),
              where('deletedAt', '==', null),
            ),
            (snapshots) => {
              result.value = {
                facilityID,
                folderID,
                tasks: snapshots.docs.map((s) => {
                  const task = s.data();
                  const parentFolderIDs = task.parentFolderIDs ?? [];
                  parentFolderIDs.push(folderID);
                  return {
                    ...task,
                    parentFolderIDs,
                  };
                }),
              };
            },
          );
        } else {
          console.error('tasksByFolder: Should not be possible to reach this branch.');
          setTimeout(() => (result.value = { facilityID, folderID, tasks: [] }), 1);
          return () => {};
        }
      });
    },

    taskGeneratorsAsFolders(facilities: Ref<Facility['facilityID'][]>): Ref<ComplianceFolderForUI[]> {
      const timestamps: StandardTimestamps & { isSystemGenerated: true } = {
        isSystemGenerated: true,
        createdAt: timestamp(),
        createdBy: 'system',
        updatedAt: timestamp(),
        updatedBy: 'system',
        deletedAt: null,
        deletedBy: null,
      };

      return useMultiQueryIn<TaskGeneratorBrief, ComplianceFolderForUI>(
        query(ComplianceDatabase.CollectionTaskGeneratorBriefs),
        'facilityID',
        facilities,
        false,
        (briefs) => {
          const folders: ComplianceFolderForUI[] = [];

          for (const brief of briefs) {
            for (const [taskGeneratorID, [title, ...parentFolderIDs]] of Object.entries(brief.taskGenerators)) {
              folders.push({
                folderID: `folder_${taskGeneratorID}`,
                name: title,
                parentFolderIDs: parentFolderIDs.length === 0 ? ['folder_unorganized'] : parentFolderIDs,
                facilityID: brief.facilityID,
                ...timestamps,
              });
            }
          }

          return folders;
        },
      ).documents;
    },

    async taskGeneratorBriefsMaintainForFacility(facilityID: Facility['facilityID']): Promise<void> {
      await backendRequest('folderTaskGeneratorBriefsMaintainForFacility', { facilityID });
      toast({ type: 'success', message: 'Briefs updated successfully!' });
    },
  },

  role: {
    async fetchByType(facilityID: Facility['facilityID'], type: Exclude<Role['type'], 'normal'>): Promise<Role | null> {
      const snapshots = await getDocs(
        query(
          ComplianceDatabase.CollectionRoles,
          where('facilityID', '==', facilityID),
          where('type', '==', type),
          limit(1),
        ),
      );
      return snapshots.docs[0]?.data() || null;
    },

    listenForAllRoles(): Ref<Role[] | null> {
      return snapshotToRef(ref(null), null as Role[] | null, (_, result) => {
        return firestoreOnSnapshot(
          query(ComplianceDatabase.CollectionRoles, where('deletedAt', '==', null)),
          (snapshots) => {
            result.value = snapshots.docs
              .map((s) => s.data())
              .sort((a, b) => {
                if (a.type !== b.type) {
                  return a.type.localeCompare(b.type);
                }

                return a.roleID.localeCompare(b.roleID);
              });
          },
        );
      });
    },

    listenForFacilityRoles(facilityRef: Ref<Facility['facilityID'] | null>): Ref<Role[] | null> {
      return snapshotToRef(facilityRef, null as Role[] | null, (newFacilityID, result) => {
        if (newFacilityID === null) {
          result.value = null;
          return () => {};
        }

        return firestoreOnSnapshot(
          query(
            ComplianceDatabase.CollectionRoles,
            and(
              or(where('facilityID', '==', newFacilityID), where('facilityID', '==', null)),
              where('deletedAt', '==', null),
            ),
          ),
          (snapshots) => {
            result.value = snapshots.docs
              .map((s) => s.data())
              .sort((a, b) => {
                if (a.type !== b.type) {
                  return a.type.localeCompare(b.type);
                }

                return a.roleID.localeCompare(b.roleID);
              });
          },
        );
      });
    },

    listenForMultipleFacilityRoles(facilitiesViewing: Ref<Facility['facilityID'][]>): UseQueryAsArray<Role> {
      const facilitySpecificResult = useMultiQueryIn<Role>(
        query(ComplianceDatabase.CollectionRoles, where('deletedAt', '==', null)),
        'facilityID',
        facilitiesViewing,
      );

      // Firestore's 'in' operator (used by useMultiQueryIn) doesn't work with mixed strings/nulls, so we do a separate
      // query:
      const globalResult = snapshotToRef(ref(null), { isLoading: true, documents: [] as Role[] }, (_, result) => {
        return firestoreOnSnapshot(
          query(ComplianceDatabase.CollectionRoles, where('facilityID', '==', null), where('deletedAt', '==', null)),
          (snapshots) => {
            result.value = { isLoading: false, documents: snapshots.docs.map((s) => s.data()) };
          },
        );
      });

      return {
        documents: computed(() => globalResult.value.documents.concat(facilitySpecificResult.documents.value)),
        isLoading: computed(() => globalResult.value.isLoading || facilitySpecificResult.isLoading.value),
      };
    },

    async create(
      role: Pick<
        Role,
        'facilityID' | 'type' | 'name' | 'owner' | 'involved' | 'escalates' | 'mustBeAssignedToEscalate'
      >,
    ) {
      return backendRequest('roleCreate', { ...role, tenantID });
    },

    async update(
      role: Pick<Role, 'roleID' | 'name' | 'owner' | 'involved' | 'escalates' | 'mustBeAssignedToEscalate'>,
    ) {
      return backendRequest('roleUpdate', { ...role, tenantID });
    },

    async delete(roleID: Role['roleID']) {
      return backendRequest('roleDelete', { tenantID, roleID });
    },

    instantiateForFacility(facilityID: Facility['facilityID']) {
      return backendRequest('rolesInstantiateForFacility', { facilityID });
    },
  },

  method9: {
    create({
      taskID,
      customFieldID,
      facilityID,
    }: {
      taskID: Task['taskID'];
      customFieldID: TaskCustomField['fieldID'];
      facilityID: Facility['facilityID'];
    }): Promise<Method9Values['method9ID']> {
      return backendRequest('method9Create', { tenantID, taskID, customFieldID, facilityID });
    },

    observationsSave(
      method9ID: Method9Values['method9ID'],
      observations: Pick<
        Method9Observations,
        'observationsStarted' | 'opacityPercentages' | 'timeBetweenReadingsInSeconds'
      >,
      stageBoundaries: Method9StageBoundary[],
    ): Promise<void> {
      return backendRequest('method9ObservationsSave', { tenantID, method9ID, observations, stageBoundaries });
    },

    stageBoundariesSave(method9ID: Method9Values['method9ID'], stageBoundaries: Method9StageBoundary[]): Promise<void> {
      return backendRequest('method9StageBoundariesSave', { tenantID, method9ID, stageBoundaries });
    },

    observationsDelete(method9ID: Method9Values['method9ID']): Promise<void> {
      return backendRequest('method9ObservationsDelete', { tenantID, method9ID });
    },

    commentSave({
      method9ID,
      commentKey,
      comment,
      type,
    }: {
      method9ID: Method9Values['method9ID'];
      commentKey: ISO8601Timestamp;
      comment: string;
      type: 'normal' | 'missed-reading';
    }): Promise<void> {
      return backendRequest('method9CommentSave', { tenantID, method9ID, commentKey, comment, type });
    },

    emissionsUnitRanSave(
      method9ID: Method9Values['method9ID'],
      emissionsUnitRanDuringDaylight: boolean,
    ): Promise<void> {
      return backendRequest('method9EmissionsUnitRanSave', { tenantID, method9ID, emissionsUnitRanDuringDaylight });
    },

    signatureSave(method9ID: Method9Values['method9ID'], signature: Method9Signature): Promise<void> {
      return backendRequest('method9SignatureSave', { tenantID, method9ID, signature });
    },

    signatureDelete(method9ID: Method9Values['method9ID']): Promise<void> {
      return backendRequest('method9SignatureDelete', { tenantID, method9ID });
    },

    diagramSave(method9ID: Method9Values['method9ID'], diagram: Method9Diagram): Promise<void> {
      return backendRequest('method9DiagramSave', { tenantID, method9ID, diagram });
    },

    formFieldSave(method9ID: Method9Values['method9ID'], field: keyof Method9Form, value: string | boolean | null) {
      return backendRequest('method9FormFieldSave', { tenantID, method9ID, field, value });
    },

    listenByCustomFieldValues(
      values: Ref<Task['customFieldValues']>,
    ): Ref<Map<Method9Values['method9ID'], Method9Values>> {
      const mapRef: Ref<Map<Method9Values['method9ID'], Method9Values>> = ref(new Map());
      const subscriptionsById = new Map<Method9Values['method9ID'], Unsubscribe>();

      watch(
        values,
        (values) => {
          const subscriptionsToRemove = new Set(Array.from(subscriptionsById.keys()));

          for (const value of Object.values(values)) {
            if (typeof value === 'string' && value.startsWith('m9v_')) {
              const method9ID = value as Method9Values['method9ID'];
              subscriptionsToRemove.delete(method9ID);

              if (!subscriptionsById.has(method9ID)) {
                subscriptionsById.set(
                  method9ID,
                  onSnapshot(doc(ComplianceDatabase.CollectionMethod9, method9ID), (snapshot) => {
                    const maybeMethod9 = snapshot.data();
                    if (maybeMethod9) {
                      mapRef.value.set(method9ID, maybeMethod9);
                    }
                  }),
                );
              }
            }
          }

          for (const method9ID of subscriptionsToRemove) {
            const unsubscribe = subscriptionsById.get(method9ID);
            if (unsubscribe) {
              unsubscribe();
            }
            subscriptionsById.delete(method9ID);
          }
        },
        { immediate: true },
      );

      onScopeDispose(() => {
        for (const unsubscribe of subscriptionsById.values()) {
          unsubscribe();
        }
      });

      return mapRef;
    },

    async fetchByCustomFieldValues(
      values: Task['customFieldValues'],
    ): Promise<Map<Method9Values['method9ID'], Method9Values>> {
      const result = new Map<Method9Values['method9ID'], Method9Values>();

      await Promise.all(
        Object.values(values).map(async (value) => {
          if (typeof value === 'string' && value.startsWith('m9v_')) {
            const method9ID = value as Method9Values['method9ID'];
            const maybeMethod9 = (await getDoc(doc(ComplianceDatabase.CollectionMethod9, method9ID))).data();
            if (maybeMethod9) {
              result.set(method9ID, maybeMethod9);
            }
          }
        }),
      );

      return result;
    },
  },

  facility: {
    async permissionsList(): Promise<FacilitiesAndPermissions> {
      if (tenantID) {
        return backendRequest('facilityPermissionsList', { tenantID });
      } else {
        return {
          facilitiesAccessible: [],
          facilitiesWithDetailsAvailable: [],
          canManageUsers: false,
          isSuperOrCompanyAdmin: false,
        };
      }
    },
  },

  help: {
    messageSend({
      subject,
      message,
      facilityID,
    }: {
      subject: string;
      message: string;
      facilityID: Facility['facilityID'];
    }): Promise<void> {
      return backendRequest('helpMessageSend', { subject, message, facilityID });
    },
  },

  holidays: {
    usFederalHolidaysList() {
      return backendRequest('usFederalHolidaysList', {});
    },
  },

  permitIndexing: {
    listenById(permitIndexingID: Ref<PermitIndexID | null>): Ref<PermitIndex | null> {
      return useDocumentReference(
        computed(function () {
          return permitIndexingID.value
            ? doc(ComplianceDatabase.CollectionPermitsIndexing, permitIndexingID.value)
            : null;
        }),
      );
    },

    async fetchByID(permitIndexingID: PermitIndexID): Promise<PermitIndex | null> {
      return (await getDoc(doc(ComplianceDatabase.CollectionPermitsIndexing, permitIndexingID))).data() ?? null;
    },

    listenByFacility(facilitiesViewing: Ref<Facility['facilityID'][]>): UseQueryAsArray<PermitIndex> {
      return useMultiQueryIn(
        computed(function () {
          return query(ComplianceDatabase.CollectionPermitsIndexing);
        }),
        'facilityID',
        facilitiesViewing,
      );
    },

    listenByTaskGeneratorId(taskGeneratorId: Ref<TaskGenerator['taskGeneratorID']>): UseQueryAsArray<PermitIndex> {
      return useQueryAsArray(
        computed(() =>
          query(
            ComplianceDatabase.CollectionPermitsIndexing,
            where('taskGeneratorIds', 'array-contains', taskGeneratorId.value),
          ),
        ),
      );
    },

    draftCreate(args: {
      facilityID: Facility['facilityID'];
      basedOnPermitIndexID: PermitIndexID;
      file: {
        fileID: `file_${string}`;
        name: string;
        categories: string[];
        sha1: string;
      };
    }) {
      return backendRequest('permitIndexDraftCreate', args);
    },

    standaloneCreate(args: {
      facilityID: Facility['facilityID'];
      file: {
        fileID: `file_${string}`;
        name: string;
        categories: string[];
        sha1: string;
        doesPermitExpire: boolean;
        expiresAt: ISO8601DateOnly | null;
      };
      canTriviallyRemoveFromIndexing: boolean;
    }) {
      return backendRequest('permitIndexStandaloneCreate', args);
    },

    skip(permitIndexID: PermitIndexID, skippedReason: string) {
      return backendRequest('permitIndexSkip', { tenantID, permitIndexID, skippedReason });
    },

    unskip(permitIndexID: PermitIndexID) {
      return backendRequest('permitIndexUnskip', { tenantID, permitIndexID });
    },

    complete(permitIndexID: PermitIndexID) {
      return backendRequest('permitIndexComplete', { tenantID, permitIndexID });
    },

    uncomplete(permitIndexID: PermitIndexID) {
      return backendRequest('permitIndexUncomplete', { tenantID, permitIndexID });
    },

    delete(permitIndexID: PermitIndexID) {
      return backendRequest('permitIndexDelete', { tenantID, permitIndexID });
    },

    async conditionFetchByID(conditionID: PermitIndexConditionID): Promise<PermitIndexCondition | null> {
      return (await getDoc(doc(ComplianceDatabase.CollectionPermitsIndexingConditions, conditionID))).data() ?? null;
    },

    async conditionsList(permitIndexID: PermitIndexID): Promise<PermitIndexCondition[]> {
      return (
        await getDocs(
          query(ComplianceDatabase.CollectionPermitsIndexingConditions, where('permitIndexID', '==', permitIndexID)),
        )
      ).docs.map((s) => s.data());
    },

    conditionsListen(
      permitIndexID: Ref<PermitIndexID | null>,
    ): Ref<Partial<Record<PermitIndexConditionID, PermitIndexCondition>>> {
      const { documents } = useQueryAsArray(
        computed(() =>
          permitIndexID.value
            ? query(
                ComplianceDatabase.CollectionPermitsIndexingConditions,
                where('permitIndexID', '==', permitIndexID.value),
              )
            : null,
        ),
      );

      return computed(() => {
        const result: Partial<Record<PermitIndexConditionID, PermitIndexCondition>> = {};
        for (const condition of documents.value) {
          result[condition.conditionID] = condition;
        }
        return result;
      });
    },

    conditionsListenToMultiple(permitIndexIds: Ref<PermitIndexID[]>): UseQueryAsArray<PermitIndexCondition> {
      return useMultiQueryIn(
        computed(function () {
          return query(ComplianceDatabase.CollectionPermitsIndexingConditions);
        }),
        'permitIndexID',
        permitIndexIds,
      );
    },

    conditionsListenByTaskGeneratorId(
      taskGeneratorID: Ref<TaskGenerator['taskGeneratorID']>,
    ): UseQueryAsArray<PermitIndexCondition> {
      return useQueryAsArray(
        computed(() =>
          query(
            ComplianceDatabase.CollectionPermitsIndexingConditions,
            where('taskAssociation.taskGeneratorIds', 'array-contains', taskGeneratorID.value),
          ),
        ),
      );
    },

    conditionSave(condition: PermitIndexCondition) {
      return backendRequest('permitIndexConditionBatchSave', { tenantID, conditions: [condition] });
    },

    conditionBatchSave(conditions: PermitIndexCondition[]) {
      return backendRequest('permitIndexConditionBatchSave', { tenantID, conditions });
    },

    updatePageFilter(
      permitIndexID: PermitIndexID,
      pageFilter: { rangeToInclude: string; rangeToExclude: string } | null,
    ) {
      return backendRequest('permitIndexUpdatePageFilter', { tenantID, permitIndexID, pageFilter });
    },

    conditionAttachTaskGenerator(args: {
      permitIndexID: PermitIndexID;
      conditionID: PermitIndexConditionID;
      taskGeneratorID: TaskGenerator['taskGeneratorID'];
    }) {
      return backendRequest('permitIndexConditionAttachGenerator', { ...args, tenantID });
    },

    emissionsUnitSave(
      permitIndexID: PermitIndexID,
      unit: Pick<PermitIndexEmissionsUnit, 'unitID' | 'name' | 'description'>,
    ) {
      return backendRequest('permitIndexEmissionsUnitSave', { tenantID, permitIndexID, unit });
    },

    emissionsUnitDelete(permitIndexID: PermitIndexID, unitID: PermitIndexUnitID) {
      return backendRequest('permitIndexEmissionsUnitDelete', { tenantID, permitIndexID, unitID });
    },

    emissionsUnitsMerge(permitIndexID: PermitIndexID, unitIDs: PermitIndexUnitID[]) {
      return backendRequest('permitIndexEmissionsUnitsMerge', { tenantID, permitIndexID, unitIDs });
    },

    forceAiProcessing(permitIndexID: PermitIndexID) {
      return backendRequest('permitIndexForceAiProcessing', { tenantID, permitIndexID });
    },

    enqueueAiProcessing() {
      return backendRequest('permitIndexEnqueueAiProcessing', { tenantID });
    },

    async reindexSinglePage(
      permitIndexID: PermitIndexID,
      pageNumber: number,
    ): Promise<{ deletedCount: number; addedCount: number }> {
      const queueTaskID = await backendRequest('permitIndexReindexSinglePage', { tenantID, permitIndexID, pageNumber });
      return new Promise((resolve, reject) => {
        const unsubscribe = onSnapshot(doc(ComplianceDatabase.CollectionBackendQueue, queueTaskID), (snapshot) => {
          const queueItem = snapshot.data();
          if (!queueItem) {
            unsubscribe();
            reject(new Error(`Queue item is missing: ${queueTaskID}`));
            return;
          }

          if (queueItem.type !== 'permit-indexing-reindex-page') {
            unsubscribe();
            reject(new Error(`Received unexpected queue item type: ${queueItem.type}`));
            return;
          }

          if (queueItem.succeededAt) {
            unsubscribe();
            resolve(queueItem.details ?? { deletedCount: 0, addedCount: 0 });
            return;
          }

          if (queueItem.failedAt) {
            unsubscribe();
            reject(new Error(queueItem.error ?? 'Unknown queue error occurred.'));
          }
        });
      });
    },

    hasActiveQueueItem(permitIndexID: Ref<PermitIndexID>): Ref<boolean> {
      return snapshotToRef(permitIndexID, false, (permitIndexID, result) => {
        return firestoreOnSnapshot(
          query(
            ComplianceDatabase.CollectionBackendQueue,
            where('args.permitIndexID', '==', permitIndexID),
            where('failedAt', '==', null),
            where('succeededAt', '==', null),
          ),
          (snapshots) => {
            result.value = snapshots.docs.length > 0;
          },
        );
      });
    },

    importGeneral(args: {
      sourcePermitIndexID: PermitIndexID;
      targetFacilityID: Facility['facilityID'];
      carryOverCompliance: boolean;
      carryOverScheduled: boolean;
    }): Promise<{ permitIndexID: PermitIndexID }> {
      return backendRequest('importGeneralPermit', { ...args, tenantID });
    },
  },

  externalIntegration: {
    listenForFacilityConfigs(facilityRef: Ref<Facility['facilityID']>): Ref<ExternalIntegrationConfig[]> {
      return snapshotToRef(facilityRef, [] as ExternalIntegrationConfig[], (facilityID, result) => {
        return firestoreOnSnapshot(
          query(
            ComplianceDatabase.CollectionExternalIntegrationConfigs,
            where('facilityID', '==', facilityID),
            where('deletedAt', '==', null),
          ),
          (snapshots) => {
            result.value = snapshots.docs.map((s) => s.data());
          },
        );
      });
    },

    async save(config: ExternalIntegrationConfig) {
      return backendRequest('externalIntegrationConfigSave', config);
    },

    async delete(externalIntegrationID: ExternalIntegrationConfig['externalIntegrationID']) {
      return backendRequest('externalIntegrationConfigDelete', { tenantID, externalIntegrationID });
    },
  },
};
